import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomModal from "../../component/common/customModal";
import { Box, Button, IconButton, Typography } from "@mui/material";

import { paymentsStatusData } from "../../../types/invoice&payment";
import invoiceService from "../../../services/InvoiceService";
import { getPaymentStatusDeduped } from "../../../services/paymentStatusPoll";
import { showToast } from "../../component/common/toastNotification";
import { emitPaymentsChanged } from "../../../services/paymentsEvents";
import { clearAbandonOnPaymentSuccess } from "../../../utils/abandonedOrderPayments";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import { jwtDecode } from "jwt-decode";
import { useSearchParams } from "react-router-dom";
import {
  getPaymentCallbackId,
  hasPendingZainCashWalletTopUp,
  isOppwaStaleSessionMessage,
  isPaymentReturnUrl,
  resolveZainCashTransactionId,
} from "../../../utils/paymentCallbackParams";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import { emitCheckoutUiEvent } from "../../../utils/checkoutEvents";
import { tryResolvePaidCheckout } from "../../../utils/checkoutPaymentResolve";
import { loadCapturedAmount, loadHeldCheckoutOrder, clearHeldCheckoutOrder } from "../../../utils/checkoutSession";
import { NJIM } from "../../../theme/njimTheme";
import {
  cancelGatewayPayment,
  releaseCheckoutGatewayPayment,
} from "../../../utils/paymentCloseCleanup";

interface Props {
  open: boolean;
  onClose: (outcome?: "review" | "history" | "step1") => void;
  /** Render as a compact page card instead of a modal overlay. */
  standalone?: boolean;
}
interface DecodedToken {
  id: string;
  transactionId?: string;
  transactionID?: string;
  txnId?: string;
  paymentId?: string;
  status?: string;
  msg?: string;
  orderid?: string;
}

type GatewayStatusPayload = {
  status?: string;
  Status?: string;
  paymentStatus?: string;
  PaymentStatus?: string;
  message?: string;
  Message?: string;
  result?: {
    status?: string;
    Status?: string;
    paymentStatus?: string;
    PaymentStatus?: string;
  };
};

const isZainCashWalletCreditPosted = async (txId: string): Promise<boolean> => {
  if (!txId) return false;
  try {
    const res = await invoiceService.getAllPayments({ silent: true });
    const rows = Array.isArray(res?.data) ? res.data : [];
    return rows.some((row) => {
      const raw = row as Record<string, unknown>;
      const id = String(raw.transactionId ?? raw.TransactionId ?? "").trim();
      const invoiceId = String(raw.invoiceId ?? raw.InvoiceId ?? "").toLowerCase();
      const status = String(raw.status ?? raw.Status ?? "").toUpperCase();
      return (
        id === txId &&
        invoiceId === "wallet" &&
        (status === "SUCCESS" || status.startsWith("SUCCESS"))
      );
    });
  } catch {
    return false;
  }
};

const PaymentStatusModal: React.FC<Props> = ({ open, onClose, standalone = false }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // ZainCash appends ?token=<jwt> to the redirect URL which already has query
  // params, producing a double-? URL that corrupts URLSearchParams. Example:
  //   /saleOrder?tab=OrderHistory&type=ZainCash?token=<jwt>
  // Here "type" reads as "ZainCash?token=<jwt>" and "token" reads as null.
  // Parse both values robustly.
  const { type, token } = useMemo(() => {
    const rawType = searchParams.get("type") ?? "";
    const qIdx = rawType.indexOf("?");
    const cleanType = (qIdx >= 0 ? rawType.slice(0, qIdx) : rawType) || null;

    let cleanToken = searchParams.get("token");
    if (!cleanToken && qIdx >= 0) {
      const embedded = new URLSearchParams(rawType.slice(qIdx + 1));
      cleanToken = embedded.get("token");
    }
    return { type: cleanType, token: cleanToken };
  }, [searchParams]);

  const [loading, setLoading] = useState(true);
  const [pollExhausted, setPollExhausted] = useState(false);
  const refreshedRef = useRef(false);
  const terminalStatusRef = useRef<"success" | "pending" | "failed" | "cancelled" | null>(null);
  const outcomeEmittedRef = useRef(false);
  const closeOutcomeRef = useRef<"review" | "history" | "step1">("history");
  const [statusData, setStatusData] = useState<paymentsStatusData>({
    status: "",
    due: "",
  });
  const decoded: DecodedToken | null = useMemo(() => {
    if (!token) return null;
    const tokenParts = token.split(".");
    if (tokenParts.length !== 3) return null;
    try {
      return jwtDecode<DecodedToken>(token);
    } catch {
      return null;
    }
  }, [token]);

  const isZainCashWalletTopUp =
    hasPendingZainCashWalletTopUp() ||
    (searchParams.get("returnTo") === "wallet" &&
      String(type ?? "").toLowerCase() === "zaincash");
  const isZainCash =
    String(type ?? "").toLowerCase() === "zaincash" || isZainCashWalletTopUp;

  // ZainCash redirect token is the most authoritative status source —
  // it's created by ZainCash at the exact moment of payment completion.
  const zainTokenStatus: "success" | "failed" | "cancelled" | null = useMemo(() => {
    if (!isZainCash || !decoded?.status) return null;
    const s = decoded.status.toLowerCase().trim();
    if (
      s.includes("success") ||
      s.includes("completed") ||
      s.includes("paid") ||
      s.includes("approved")
    )
      return "success";
    if (s.includes("cancel")) return "cancelled";
    if (
      s.includes("failed") ||
      s.includes("declined") ||
      s.includes("rejected")
    )
      return "failed";
    return null;
  }, [decoded?.status, isZainCash]);

  const isHyperPaySuccess = (code?: string): boolean => 
    !!code && /^(000\.000\.|000\.100\.1|000\.[36]|000\.400\.[1][12]0)/.test(code);

  const isHyperPayPending = (code?: string) =>
    !!code && code.startsWith("000.200");

  const normalizeGatewayStatus = (value: string) => {
    const normalized = String(value ?? "").trim().toLowerCase();
    if (!normalized) return "pending";
    if (
      normalized.includes("success") ||
      normalized.includes("completed") ||
      normalized.includes("paid") ||
      normalized.includes("approved")
    ) {
      return "success";
    }
    if (normalized.includes("cancel")) {
      return "cancelled";
    }
    if (
      normalized.includes("failed") ||
      normalized.includes("declined") ||
      normalized.includes("rejected")
    ) {
      return "failed";
    }
    return "pending";
  };

  const extractGatewayStatus = (data: GatewayStatusPayload) => {
    return (
      data?.status ??
      data?.Status ??
      data?.paymentStatus ??
      data?.PaymentStatus ??
      data?.result?.status ??
      data?.result?.Status ??
      data?.result?.paymentStatus ??
      data?.result?.PaymentStatus ??
      data?.message ??
      data?.Message ??
      ""
    );
  };

  const handlePaymentStatus = useCallback(async (): Promise<"success" | "pending" | "failed" | "cancelled"> => {
    if (terminalStatusRef.current === "success") {
      return "success";
    }

    try {
      // Wallet credit only happens inside CheckStatus with the recharge transaction
      // id (same as the mobile app). Never treat the ZainCash JWT as top-up success,
      // and never overwrite that stored id with JWT `id`.
      if (zainTokenStatus === "cancelled" || zainTokenStatus === "failed") {
        const txId = resolveZainCashTransactionId(decoded);

        setStatusData({
          status: zainTokenStatus,
          due:
            zainTokenStatus === "cancelled"
              ? (t("salesOrder.zaincashCancelled", {
                  defaultValue: "Payment cancelled. No charge was taken.",
                }) as string)
              : (t("invoicesPayments.paymentFailed") as string),
        });

        if (txId) {
          invoiceService
            .getStatusDetailsData("ZainCash", txId, { silent: true })
            .catch(() => {});
        }
        if (!isZainCashWalletTopUp) {
          localStorage.removeItem("lastZainCashTransactionId");
        }
        return zainTokenStatus;
      }

      if (zainTokenStatus === "success" && !isZainCashWalletTopUp) {
        const txId = resolveZainCashTransactionId(decoded);

        setStatusData({
          status: "success",
          due: t("invoicesPayments.paymentSuccessful") as string,
        });

        if (txId) {
          invoiceService
            .getStatusDetailsData("ZainCash", txId, { silent: true })
            .catch(() => {});
        }
        localStorage.removeItem("lastZainCashTransactionId");
        return "success";
      }

      if (zainTokenStatus === "success" && isZainCashWalletTopUp) {
        setStatusData({
          status: "pending",
          due: t("qicard.walletPendingMessage", {
            defaultValue: "Please wait while we confirm your wallet top-up.",
          }) as string,
        });
      }

      const urlCallbackId = getPaymentCallbackId(searchParams);
      const cachedCardTopUpTxId = localStorage.getItem("lastCardTopUpTransactionId") ?? "";
      const cachedCardPaymentTxId = localStorage.getItem("lastCardPaymentTransactionId") ?? "";
      const isPaymentReturn = isPaymentReturnUrl(searchParams);

      let transactionId = urlCallbackId;
      if (isZainCashWalletTopUp) {
        transactionId = resolveZainCashTransactionId(decoded);
      } else if (!transactionId && type === "CardPayment" && isPaymentReturn) {
        transactionId = cachedCardTopUpTxId || cachedCardPaymentTxId;
      } else if (!transactionId && type === "Wallet" && isPaymentReturn) {
        transactionId = cachedCardTopUpTxId;
      } else if (!transactionId && isZainCash) {
        transactionId = resolveZainCashTransactionId(decoded);
      }

      if (type === "CardPayment" && !isPaymentReturn) {
        setStatusData({
          status: "pending",
          due: t("invoicesPayments.paymentPending", {
            defaultValue: "Payment is being processed. Please wait a moment.",
          }) as string,
        });
        return "pending";
      }

      const input = {
        type: (type ?? "") || (isZainCashWalletTopUp ? "ZainCash" : ""),
        transactionId: String(transactionId ?? "").trim(),
      };

      const applyPaidCheckoutSuccess = async (
        txId: string
      ): Promise<"success" | null> => {
        const { paid, orderId } = await tryResolvePaidCheckout(txId);
        if (!paid) return null;
        terminalStatusRef.current = "success";
        localStorage.removeItem("lastCardPaymentTransactionId");
        localStorage.removeItem("lastCardTopUpTransactionId");
        clearAbandonOnPaymentSuccess(orderId, txId || null);
        setStatusData({
          status: "success",
          due: t("invoicesPayments.paymentSuccessful") as string,
        });
        return "success";
      };

      if (!input.type || !input.transactionId) {
        if (type === "CardPayment" && isPaymentReturn) {
          const resolved = await applyPaidCheckoutSuccess("");
          if (resolved === "success") return "success";
        }
        setStatusData({
          status: "pending",
          due: isZainCashWalletTopUp
            ? (t("qicard.walletPendingMessage", {
                defaultValue: "Please wait while we confirm your wallet top-up.",
              }) as string)
            : (t("invoicesPayments.paymentPending", {
                defaultValue: "Payment is being processed. Please wait a moment.",
              }) as string),
        });
        return "pending";
      }

      const response = await getPaymentStatusDeduped(
        input.type,
        input.transactionId
      );

      // ---------------- CARD PAYMENT (HyperPay) ----------------
      if (type === "CardPayment") {
        const { code, description } = response.data as {
          code?: string;
          description?: string;
        };

        if (isOppwaStaleSessionMessage(description)) {
          const resolved = await applyPaidCheckoutSuccess(input.transactionId);
          if (resolved === "success") return "success";
          localStorage.removeItem("lastCardPaymentTransactionId");
          localStorage.removeItem("lastCardTopUpTransactionId");
          // The checkout is dead at HyperPay, but the server may still hold it as PENDING,
          // which blocks the next attempt on this order. Release it so "start again" works.
          void cancelGatewayPayment(input.transactionId).then((outcome) => {
            if (outcome === "completed") void applyPaidCheckoutSuccess(input.transactionId);
          });
          setStatusData({
            status: "pending",
            due: t("invoicesPayments.cardSessionExpired", {
              defaultValue:
                "This card payment session expired. Please close and start payment again.",
            }) as string,
          });
          return "pending";
        }

        let status = "pending";
        let due = description ?? "";

        if (isHyperPaySuccess(code)) {
          status = "success";
          due = description ?? "";
        } else if (isHyperPayPending(code)) {
          status = "pending";
          due = description ?? "";
        } else {
          status = "failed";
          due = description || t("invoicesPayments.paymentFailed");
        }

        if (status !== "success") {
          const resolved = await applyPaidCheckoutSuccess(input.transactionId);
          if (resolved === "success") return "success";
        }

        setStatusData({ status, due });
        if (status === "success") {
          terminalStatusRef.current = "success";
          localStorage.removeItem("lastCardPaymentTransactionId");
          localStorage.removeItem("lastCardTopUpTransactionId");
        } else if (status === "failed") {
          terminalStatusRef.current = "failed";
        }
        return status as "success" | "pending" | "failed";
      } else if (type === "Wallet") {
        const apiStatus = response.data;

        const status =
          typeof apiStatus === "string" && apiStatus.toUpperCase() === "SUCCESS"
            ? "success"
            : "failed";

        const due =
          status === "success"
            ? t("invoicesPayments.paymentSuccessful")
            : t("invoicesPayments.paymentFailed");

        setStatusData({ status, due });
        if (status === "success") {
          localStorage.removeItem("lastCardTopUpTransactionId");
        }
        return status as "success" | "failed";
      }

      // ---------------- ZAINCASH (no redirect token) ----------------
      const zainStatusRaw = extractGatewayStatus(
        response.data as GatewayStatusPayload
      );
      const mappedStatus = normalizeGatewayStatus(String(zainStatusRaw));
      const pendingDue = isZainCashWalletTopUp
        ? (t("qicard.walletPendingMessage", {
            defaultValue: "Please wait while we confirm your wallet top-up.",
          }) as string)
        : (t("invoicesPayments.paymentPending", {
            defaultValue: "Payment is being processed. Please wait a moment.",
          }) as string);
      const mappedDue =
        mappedStatus === "success"
          ? t("invoicesPayments.paymentSuccessful")
          : mappedStatus === "cancelled"
            ? t("salesOrder.zaincashCancelled", {
                defaultValue: "Payment cancelled. No charge was taken.",
              })
          : mappedStatus === "failed"
            ? t("invoicesPayments.paymentFailed")
            : pendingDue;

      if (isZainCashWalletTopUp && mappedStatus === "success") {
        const posted = await isZainCashWalletCreditPosted(input.transactionId);
        if (!posted) {
          setStatusData({
            status: "pending",
            due: pendingDue,
          });
          return "pending";
        }
        terminalStatusRef.current = "success";
        setStatusData({
          status: "success",
          due: t("invoicesPayments.paymentSuccessful") as string,
        });
        return "success";
      }

      setStatusData({
        status: mappedStatus,
        due: mappedDue,
      });
      if (isZainCash && mappedStatus === "success") {
        localStorage.removeItem("lastZainCashTransactionId");
      }
      return mappedStatus as "success" | "pending" | "failed" | "cancelled";
    } catch {
      if (isZainCash) {
        const walletTopUpPending =
          searchParams.get("returnTo") === "wallet" ||
          localStorage.getItem("lastZainCashWalletTopUp") === "1";
        setStatusData({
          status: "pending",
          due: walletTopUpPending
            ? (t("qicard.walletPendingMessage", {
                defaultValue: "Please wait while we confirm your wallet top-up.",
              }) as string)
            : (t("invoicesPayments.paymentPending", {
                defaultValue: "Payment is being processed. Please wait a moment.",
              }) as string),
        });
        return "pending";
      }
      showToast(t("invoicesPayments.paymentFailed"), "error");
      setStatusData({
        status: "failed",
        due: t("invoicesPayments.paymentFailed"),
      });
      return "failed";
    } finally {
      setLoading(false);
    }
  }, [decoded, searchParams, t, type, isZainCash, isZainCashWalletTopUp, zainTokenStatus]);

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setPollExhausted(false);
    let attempts = 0;
    const isWalletTopUp = isZainCashWalletTopUp;
    const maxAttempts = isWalletTopUp ? 40 : 16;
    const pollDelay = isWalletTopUp ? 3000 : 2500;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const runPoll = async () => {
      attempts += 1;
      const currentStatus = await handlePaymentStatus();
      const shouldStop =
        currentStatus === "success" ||
        currentStatus === "cancelled" ||
        // For ZainCash without token confirmation, keep retrying on "failed"
        // because the backend may get a transient error from ZainCash's API.
        (!isZainCash && currentStatus === "failed") ||
        attempts >= maxAttempts;
      if (shouldStop) {
        if (attempts >= maxAttempts && currentStatus === "pending") {
          setPollExhausted(true);
        }
        return;
      }
      timer = setTimeout(runPoll, pollDelay);
    };

    timer = setTimeout(runPoll, 1500);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [open, handlePaymentStatus, isZainCash, isZainCashWalletTopUp, searchParams]);

  const normalizedStatus = statusData?.status?.toLowerCase();

  const formatStatusMessage = useCallback(
    (message: string) => {
      const trimmed = String(message ?? "").trim();
      if (!trimmed || /integrator test mode|merchant in integrator/i.test(trimmed)) {
        if (normalizedStatus === "success" || normalizedStatus === "completed") {
          return t("invoicesPayments.paymentSuccessfulDetail", {
            defaultValue: "Your payment has been processed successfully.",
          });
        }
        if (normalizedStatus === "cancelled") {
          return t("salesOrder.zaincashCancelled", {
            defaultValue: "Payment cancelled. No charge was taken.",
          });
        }
        if (normalizedStatus === "failed") {
          return t("invoicesPayments.paymentFailed");
        }
        if (pollExhausted) {
          return t("salesOrder.unresolvedPayment", {
            defaultValue:
              "We haven’t had confirmation yet. Check Invoices & Payments shortly.",
          });
        }
      }
      return trimmed;
    },
    [normalizedStatus, pollExhausted, t]
  );

  useEffect(() => {
    if (loading) return;
    const isSuccess = normalizedStatus === "success" || normalizedStatus === "completed";
    if (isSuccess && !refreshedRef.current) {
      refreshedRef.current = true;
      const txId =
        getPaymentCallbackId(searchParams) ??
        localStorage.getItem("lastCardPaymentTransactionId") ??
        localStorage.getItem("lastZainCashTransactionId") ??
        "";
      clearAbandonOnPaymentSuccess(null, txId || null);
      emitPaymentsChanged();
    }

    if (outcomeEmittedRef.current) return;
    if (type === "Wallet" || searchParams.get("returnTo") === "wallet") return;

    const held = loadHeldCheckoutOrder();
    const isCheckout = Boolean(held?.orderId);
    const isSaleOrderReturn =
      searchParams.get("returnTo") !== "wallet" &&
      searchParams.get("returnTo") !== "dashboard";
    const amount = loadCapturedAmount();

    const mark = (landing: "review" | "history" | "step1") => {
      outcomeEmittedRef.current = true;
      closeOutcomeRef.current = landing;
    };

    if (isSuccess) {
      if (isCheckout || isSaleOrderReturn) {
        emitCheckoutUiEvent({
          type: "success",
          amount,
          orderId: held?.orderId,
          landOn: "step1",
        });
        clearHeldCheckoutOrder();
      }
      mark(isCheckout || isSaleOrderReturn ? "step1" : "history");
      return;
    }

    if (normalizedStatus === "cancelled" || normalizedStatus === "failed") {
      const txId =
        getPaymentCallbackId(searchParams) ??
        localStorage.getItem("lastZainCashTransactionId") ??
        localStorage.getItem("lastCardPaymentTransactionId") ??
        "";
      const gatewayType = isZainCash
        ? "ZainCash"
        : type === "CardPayment" || type === "QiCard"
          ? type
          : null;
      if (txId && gatewayType) {
        void releaseCheckoutGatewayPayment({
          type: gatewayType,
          transactionId: txId,
        });
      }
      if (isCheckout) {
        emitCheckoutUiEvent({ type: "keep_review" });
        mark("review");
      } else {
        mark("history");
      }
      return;
    }

    if (pollExhausted) {
      if (type === "CardPayment" && isCheckout) {
        const txId =
          getPaymentCallbackId(searchParams) ??
          localStorage.getItem("lastCardPaymentTransactionId") ??
          "";
        void tryResolvePaidCheckout(txId).then(({ paid }) => {
          if (outcomeEmittedRef.current) return;
          if (paid) {
            emitCheckoutUiEvent({
              type: "success",
              amount,
              orderId: held?.orderId,
              landOn: "step1",
            });
            mark("step1");
            return;
          }
          emitCheckoutUiEvent({ type: "card_pending", amount });
          mark("history");
        });
        return;
      } else if (isCheckout) {
        emitCheckoutUiEvent({ type: "keep_review" });
        mark("review");
      } else {
        mark("history");
      }
    }
  }, [loading, normalizedStatus, pollExhausted, searchParams, type, isZainCash]);

  useEffect(() => {
    if (!open) {
      refreshedRef.current = false;
      terminalStatusRef.current = null;
      outcomeEmittedRef.current = false;
      closeOutcomeRef.current = "history";
      setPollExhausted(false);
    }
  }, [open]);

  const statusPalette = useMemo(() => {
    switch (normalizedStatus) {
      case "failed":
        return { ring: "rgba(229,72,77,0.12)", icon: NJIM.danger, title: t("invoicesPayments.paymentFailed") };
      case "cancelled":
        return {
          ring: "rgba(113,128,155,0.12)",
          icon: NJIM.textSecondary,
          title: t("salesOrder.paymentCancelledTitle", { defaultValue: "Payment cancelled" }),
        };
      case "success":
      case "completed":
        return { ring: "rgba(93,183,167,0.18)", icon: NJIM.success, title: t("invoicesPayments.paymentSuccessful") };
      default:
        return { ring: "rgba(255,152,0,0.12)", icon: NJIM.warning, title: t("invoicesPayments.paymentPending", { defaultValue: "Payment Pending" }) };
    }
  }, [normalizedStatus, t]);

  const renderIcon = () => {
    const iconSize = 44;
    switch (normalizedStatus) {
      case "pending":
        return <PendingIcon sx={{ fontSize: iconSize, color: statusPalette.icon }} />;
      case "failed":
        return <CancelIcon sx={{ fontSize: iconSize, color: statusPalette.icon }} />;
      case "cancelled":
        return <CancelIcon sx={{ fontSize: iconSize, color: statusPalette.icon }} />;
      case "success":
      case "completed":
        return <CheckCircleIcon sx={{ fontSize: iconSize, color: statusPalette.icon }} />;
      default:
        return <PendingIcon sx={{ fontSize: iconSize, color: statusPalette.icon }} />;
    }
  };

  const renderTitle = () => {
    if (loading) {
      return t("invoicesPayments.processingPayment", { defaultValue: "Processing..." });
    }
    return statusPalette.title;
  };

  const releaseUnpaidGatewayPayment = () => {
    const settled =
      normalizedStatus === "success" || normalizedStatus === "completed";
    if (settled) return;

    const urlId = getPaymentCallbackId(searchParams);
    const transactionId = (
      isZainCash
        ? resolveZainCashTransactionId(decoded) ||
          urlId ||
          localStorage.getItem("lastZainCashTransactionId")
        : type === "CardPayment"
          ? urlId ||
            localStorage.getItem("lastCardPaymentTransactionId") ||
            localStorage.getItem("lastCardTopUpTransactionId")
          : type === "QiCard"
            ? urlId
            : ""
    )?.trim();

    if (!transactionId) return;
    void cancelGatewayPayment(transactionId);
  };

  const dismiss = () => {
    releaseUnpaidGatewayPayment();
    onClose(closeOutcomeRef.current);
  };

  const panelContent = (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        p: 4,
        width: "100%",
        boxSizing: "border-box",
        minHeight: { xs: 300, sm: 320 },
      }}
    >
      {!standalone && (
        <IconButton
          onClick={dismiss}
          aria-label={t("common.close", { defaultValue: "Close" })}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 34,
            height: 34,
            borderRadius: "10px",
            bgcolor: NJIM.muted,
            border: `1px solid ${NJIM.border}`,
            color: NJIM.textSecondary,
            "&:hover": { bgcolor: NJIM.muted },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: 300,
          gap: 2,
          flex: 1,
        }}
      >
        <Box
          sx={{
            width: 76,
            height: 76,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: statusPalette.ring,
            flexShrink: 0,
          }}
        >
          {loading ? (
            <PendingIcon sx={{ fontSize: 44, color: NJIM.warning }} />
          ) : (
            renderIcon()
          )}
        </Box>

        <Typography sx={{ fontWeight: 700, fontSize: "20px", color: NJIM.ink, lineHeight: 1.3, m: 0 }}>
          {renderTitle()}
        </Typography>

        <Typography
          sx={{
            color: NJIM.textSecondary,
            fontSize: "14px",
            lineHeight: 1.55,
            m: 0,
            px: 0.5,
          }}
        >
          {loading
            ? t("invoicesPayments.fetchingPaymentStatus", {
                defaultValue: "Please wait while we confirm your payment.",
              })
            : formatStatusMessage(statusData.due ?? "")}
        </Typography>

        <Button
          variant="contained"
          disabled={loading}
          fullWidth
          sx={{
            mt: 0.5,
            background: NJIM.walletHero,
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "15px",
            py: 1.25,
            boxShadow: "0 8px 20px rgba(22,45,58,0.22)",
            "&:hover": {
              boxShadow: "0 10px 24px rgba(22,45,58,0.28)",
              background: NJIM.walletHeroHover,
            },
            "&.Mui-disabled": {
              background: "rgba(0,0,0,0.08)",
              color: "rgba(0,0,0,0.38)",
            },
          }}
          onClick={() => {
            if (normalizedStatus === "success" || normalizedStatus === "completed") {
              const txId =
                getPaymentCallbackId(searchParams) ??
                localStorage.getItem("lastCardPaymentTransactionId") ??
                localStorage.getItem("lastZainCashTransactionId") ??
                "";
              clearAbandonOnPaymentSuccess(null, txId || null);
              emitPaymentsChanged();
            } else {
              releaseUnpaidGatewayPayment();
            }
            localStorage.removeItem("lastCardTopUpTransactionId");
            localStorage.removeItem("lastCardPaymentTransactionId");
            onClose(closeOutcomeRef.current);
          }}
        >
          {t("common.ok", { defaultValue: "OK" })}
        </Button>
      </Box>
    </Box>
  );

  if (standalone) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 360,
          bgcolor: NJIM.white,
          borderRadius: "16px",
          border: `1px solid ${NJIM.border}`,
          boxShadow: "0 16px 40px rgba(23,34,55,0.1)",
        }}
      >
        {panelContent}
      </Box>
    );
  }

  return (
    <CustomModal
      open={open}
      onClose={dismiss}
      hideHeader
      width={{ xs: "90%", sm: 380 }}
      paperSx={{
        p: 0,
        bgcolor: NJIM.white,
        borderRadius: "18px",
        border: `1px solid ${NJIM.border}`,
        boxShadow: "0 24px 48px rgba(22,45,58,0.16)",
        overflow: "hidden",
      }}
    >
      {panelContent}
    </CustomModal>
  );
};

export default PaymentStatusModal;
